import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { getBackendUrl } from "@/lib/backend";
import {
  buildWaterSafetyLocalGuidance,
  buildWaterSafetyMetricsPayload,
} from "@/utils/buildWaterSafetyLocalGuidance";

const API = `${getBackendUrl()}/api`;

export function useWaterSafetyAiGuidance({
  overview,
  blockData,
  bottomBlocks,
  highRiskSchools,
  inclusionMetrics,
  scopeLabel,
  scopeKey,
}) {
  const [guidance, setGuidance] = useState(null);
  const [provider, setProvider] = useState("local");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [generatedAt, setGeneratedAt] = useState(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    if (!overview?.total_schools) {
      setGuidance(null);
      setProvider("local");
      setLoading(false);
      setError(null);
      return;
    }

    const localGuidance = buildWaterSafetyLocalGuidance({
      overview,
      blockData,
      bottomBlocks,
      highRiskSchools,
      inclusionMetrics,
    });
    setError(null);

    const requestId = ++requestIdRef.current;
    const metrics = buildWaterSafetyMetricsPayload({
      overview,
      blockData,
      bottomBlocks,
      highRiskSchools,
      scopeLabel,
    });

    setLoading(true);
    axios
      .post(`${API}/infrastructure/ai-guidance`, {
        metrics,
        local_guidance: localGuidance,
      })
      .then((res) => {
        if (requestId !== requestIdRef.current) return;
        if (res.data?.guidance) {
          setGuidance(res.data.guidance);
          setProvider(res.data.provider || "local");
          setGeneratedAt(res.data.generated_at || null);
        }
      })
      .catch((err) => {
        if (requestId !== requestIdRef.current) return;
        setError(err?.response?.data?.detail || err.message || "AI guidance unavailable");
        setGuidance(localGuidance);
        setProvider("local");
      })
      .finally(() => {
        if (requestId === requestIdRef.current) {
          setLoading(false);
        }
      });
  }, [
    overview,
    blockData,
    bottomBlocks,
    highRiskSchools,
    inclusionMetrics,
    scopeLabel,
    scopeKey,
  ]);

  return {
    guidance,
    provider,
    loading,
    error,
    generatedAt,
    isMistral: provider === "mistral",
  };
}
